import { requestApi } from 'api/Api';

import { iaRa } from 'lib/helpers/utility';
import { formatDate } from 'lib/helpers/dateUtils';

import { showAlert } from 'components/shared/Alert/Alert';

export const sendEmail = async ({
  id,
  navigate,
  onSuccess = () => {},
  onError = () => {},
  toAltEmail,
  customEmail = null
}) => {
  try {
    return await requestApi({
      url: '/api/practice/billing/invoicing/email',
      params: {
        invoiceId: id,
        toAltEmail,
        customEmail
      },
      navigate,
      onSuccess,
      onError
    });
  } catch (error) {
    console.error(error);
  }
};
export const sendText = async ({
  id,
  navigate,
  onSuccess = () => {},
  onError = () => {},
  toAltPhone,
  customPhone = null
}) => {
  try {
    return await requestApi({
      url: '/api/practice/billing/invoicing/text',
      params: {
        invoiceId: id,
        toAltPhone,
        customPhone
      },
      navigate,
      onSuccess,
      onError
    });
  } catch (error) {
    console.error(error);
  }
};

export const getInvoicesFromSelectedRows = (selectedRowData, externalInvoiceId) => {
  const finalInvoices = iaRa(selectedRowData).reduce((acc, invoice) => {
    const { patient_id, dateofservice, last_applied, appointment } = invoice;
    const defaultShow = invoice?.show ?? true;
    const groupKey = patient_id ?? `no_patient_${invoice.id}`;

    if (!acc?.[groupKey]) {
      acc[groupKey] = {
        total_amount: 0,
        amount_paid: 0,
        invoice_balance: 0,
        discount: 0,
        procedures: [],
        packages: [],
        products: [],
        tax: 0,
        internal_invoice_ids: [],
        created_at: invoice?.created_at,
        dateofservice: invoice?.dateofservice,
        appointment,
        write_off: 0,
        status: invoice?.status,
        patient_id,
        aeob: invoice?.aeob ?? false,
        show: defaultShow,
        ...(invoice?.link && { link: invoice?.link })
      };
    }

    const patientInvoice = acc[groupKey];

    const balance = invoice?.invoice_balance ?? invoice?.balance ?? 0;

    patientInvoice.internal_invoice_ids.push(invoice.id);
    patientInvoice.id = externalInvoiceId || invoice.id;
    patientInvoice.patient = invoice.patient;
    patientInvoice.total_amount += balance;
    patientInvoice.amount_paid += Number(invoice?.amount_paid ?? 0);
    patientInvoice.invoice_balance += balance;
    patientInvoice.tax += Number(invoice?.tax ?? 0);
    patientInvoice.write_off += Number(invoice?.write_off ?? 0);
    patientInvoice.aeob = patientInvoice.aeob || (invoice?.aeob ?? false);
    if (invoice.discount) patientInvoice.discount = invoice?.discount;

    invoice?.procedures?.length > 0 &&
      patientInvoice.procedures.push(
        ...invoice.procedures.map((procedure) => ({
          ...procedure,
          dateofservice,
          last_applied,
          invoiceId: procedure.invoiceId || invoice.id,
          invoiceStatus: invoice.status,
          show: defaultShow
        }))
      );
    invoice?.products?.length > 0 &&
      patientInvoice.products.push(
        ...invoice.products.map((product) => ({
          ...product,
          dateofservice,
          invoiceId: invoice.id,
          invoiceStatus: invoice.status,
          show: defaultShow
        }))
      );
    invoice?.packages?.length > 0 &&
      patientInvoice.packages.push(
        ...invoice.packages.map((pkg) => ({
          ...pkg,
          dateofservice,
          invoiceId: invoice.id,
          invoiceStatus: invoice.status,
          show: defaultShow
        }))
      );
    patientInvoice.created_at = patientInvoice?.created_at || formatDate({ data: new Date() });
    patientInvoice.dateofservice =
      patientInvoice?.dateofservice || formatDate({ data: new Date() });

    return acc;
  }, {});

  return Object.values(finalInvoices);
};
export const onSendEmail = async ({
  ids = [],
  setButton = () => {},
  navigate,
  toAltEmail = false,
  isPopover = false,
  customEmail = null
}) => {
  let successEmailsCount = 0;
  if (!isPopover) setButton((prev) => ({ ...prev, loading: true }));

  const onSuccess = (res) => {
    if (res?.success) ++successEmailsCount;
    if (!isPopover) setButton((prev) => ({ ...prev, label: 'Sent', loading: false }));
  };
  const onError = (error) => {
    showAlert({
      title: "Couldn't send email",
      message: error,
      color: 'danger'
    });
    if (!isPopover) setButton((prev) => ({ ...prev, loading: false }));
  };
  const sendEmailPromises = ids.map((id) =>
    sendEmail({ id, navigate, onSuccess, onError, toAltEmail, customEmail })
  );
  await Promise.all(sendEmailPromises);
  if (successEmailsCount > 0) {
    showAlert({
      title: successEmailsCount > 1 ? `${successEmailsCount} invoices sent` : 'Invoice sent',
      color: 'success'
    });
  }

  return { code: successEmailsCount > 0 ? 0 : 1 };
};

export const onSendText = async ({
  ids = [],
  setButton = () => {},
  navigate,
  toAltPhone = false,
  isPopover = false,
  customPhone = null
}) => {
  let successMessagesCount = 0;
  if (!isPopover) {
    setButton((prev) => ({ ...prev, loading: true }));
  }

  const onSuccess = (res) => {
    if (res?.success) ++successMessagesCount;

    if (!isPopover) setButton((prev) => ({ ...prev, label: 'Sent', loading: false }));
  };
  const onError = (error) => {
    showAlert({
      title: "Couldn't send text",
      message: error,
      color: 'danger'
    });
    if (!isPopover) setButton((prev) => ({ ...prev, loading: false }));
  };

  const sendEmailPromises = ids.map((id) =>
    sendText({ id, navigate, onSuccess, onError, toAltPhone, customPhone })
  );
  await Promise.all(sendEmailPromises);

  if (successMessagesCount > 0) {
    showAlert({
      title: successMessagesCount > 1 ? `${successMessagesCount} invoices sent` : 'Invoice sent',
      color: 'success'
    });
  }

  return { code: successMessagesCount > 0 ? 0 : 1 };
};
